import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetAccountsPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the accounts index page.
 *
 * Fetches all data the page needs in a single HTTP round trip on client-side
 * navigation. The loader then seeds individual TanStack Query caches via
 * setQueryData so components refetch independently after the first paint.
 *
 * During SSR this runs as a direct function call — no HTTP involved either way.
 */
export const getAccountsPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetAccountsPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.pageData", async () => {
      const [businessMod, accountMod] = await Promise.all([
        import("../business/business.server"),
        import("./account.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { listAccountsForUser, listAllAccountsForUser } = accountMod;

      const [businessDetail, accountsList, allAccounts] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        listAccountsForUser(context.user.id, data.businessId, { limit: 20 }),
        listAllAccountsForUser(context.user.id, data.businessId),
      ]);

      return { businessDetail, accountsList, allAccounts } as const;
    });
  });
